import { type FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Panel } from '../components/Panel.js';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  ErrorNote,
  Field,
  FileField,
  Icon,
  type IconName,
  Modal,
  PageHeader,
  PagedTableShell,
  Select,
  Stat,
  Table,
  Tabs,
  Td,
  TextInput,
  Textarea,
  cx,
  useConfirm,
  useClientPaging,
} from '../components/ui/index.js';
import { readJson, useRemote } from '../lib/remote.js';
import { openAuthenticatedPdf, uploadRawJson } from '../lib/api.js';
import { useUiPrefs } from '../lib/ui-prefs.js';
import { useAdminLocale } from '../lib/admin-locale.js';

interface SiteRow {
  id: string;
  name: string;
  canonicalOrigin: string;
}

interface SourceRow {
  id: string;
  type: string;
  title: string;
  locale: string | null;
  status: string;
  activeVersionId: string | null;
  canonicalUrl?: string | null;
  origin: 'WORDPRESS' | 'MANUAL_UPLOAD' | 'MANUAL_ENTRY';
  canDelete: boolean;
  updatedAt?: string;
  latestVersionStatus: string | null;
}

interface SourcePreview {
  title: string;
  status: string;
  origin: 'WORDPRESS' | 'MANUAL_UPLOAD' | 'MANUAL_ENTRY';
  canDelete: boolean;
  preview: { revision: number; status: string; text: string; errorCode: string | null } | null;
  canAiExtract?: boolean;
  pdfAiRecommendation?: {
    decision: 'NOT_NEEDED' | 'RECOMMENDED' | 'REQUIRED';
    reason: 'TEXT_OK' | 'QUALITY_LOW' | 'EMPTY_TEXT';
  } | null;
}

type Kind = 'all' | 'pages' | 'pdf' | 'faq' | 'text';
type Tone = 'ready' | 'processing' | 'failed' | 'inactive';

type TextFn = (el: string, en: string) => string;

function kindItems(text: TextFn): Array<{ id: Kind; label: string; icon: IconName }> {
  return [
    { id: 'all', label: text('Όλες οι πηγές', 'All sources'), icon: 'inbox' },
    { id: 'pages', label: text('Σελίδες website', 'Website pages'), icon: 'globe' },
    { id: 'pdf', label: text('Αρχεία PDF', 'PDF files'), icon: 'file' },
    { id: 'faq', label: text('Συχνές ερωτήσεις', 'Frequently asked questions'), icon: 'help' },
    { id: 'text', label: text('Κείμενα', 'Text entries'), icon: 'layers' },
  ];
}

function typeLabel(type: string, text: TextFn): string {
  return (
    {
      WP_PAGE: text('Σελίδα', 'Page'),
      WP_POST: text('Άρθρο', 'Post'),
      WP_PDF: 'WordPress PDF',
      PDF: 'PDF',
      FAQ: text('Συχνή ερώτηση', 'FAQ'),
      TEXT: text('Κείμενο', 'Text'),
    }[type] ?? type
  );
}

function localeLabel(locale: string, text: TextFn): string {
  return (
    {
      el: text('Ελληνικά', 'Greek'),
      en: text('Αγγλικά', 'English'),
      fr: text('Γαλλικά', 'French'),
    }[locale] ?? locale
  );
}

function originLabel(origin: SourceRow['origin'], text: TextFn): string {
  return {
    WORDPRESS: 'Website / WordPress',
    MANUAL_UPLOAD: text('Ανέβασμα διαχειριστή', 'Administrator upload'),
    MANUAL_ENTRY: text('Χειροκίνητη καταχώριση', 'Manual entry'),
  }[origin];
}

function toneLabel(tone: Tone, text: TextFn): string {
  return {
    ready: text('Έτοιμο', 'Ready'),
    processing: text('Σε επεξεργασία', 'Processing'),
    failed: text('Αποτυχία', 'Failed'),
    inactive: text('Ανενεργό', 'Inactive'),
  }[tone];
}

const TONE_BADGE: Record<Tone, string> = {
  ready: 'READY',
  processing: 'PENDING',
  failed: 'FAILED',
  inactive: 'DISABLED',
};

function sourceTone(source: SourceRow): Tone {
  if (source.latestVersionStatus === 'FAILED') return 'failed';
  if (source.status === 'DISABLED') return 'inactive';
  if (
    source.status === 'ACTIVE' &&
    source.latestVersionStatus === 'READY' &&
    source.activeVersionId
  ) {
    return 'ready';
  }
  return 'processing';
}

function typeIcon(type: string): IconName {
  if (type === 'PDF' || type === 'WP_PDF') return 'file';
  if (type === 'FAQ') return 'help';
  if (type === 'TEXT') return 'layers';
  return 'globe';
}

function toneClass(tone: Tone): string {
  if (tone === 'failed') return 'bg-rose-50 dark:bg-rose-950/40 text-rose-600';
  if (tone === 'ready') return 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600';
  if (tone === 'inactive')
    return 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 dark:text-slate-500';
  return 'bg-blue-50 dark:bg-blue-950/40 text-blue-600';
}

function pdfAiMessage(
  verdict:
    | {
        decision: 'NOT_NEEDED' | 'RECOMMENDED' | 'REQUIRED';
        reason: 'TEXT_OK' | 'QUALITY_LOW' | 'EMPTY_TEXT';
      }
    | null
    | undefined,
  text: TextFn,
): string | null {
  if (!verdict) return null;
  if (verdict.reason === 'EMPTY_TEXT')
    return text(
      'Δεν υπάρχει αξιοποιήσιμο κείμενο. Η εξαγωγή με AI χρειάζεται για αυτό το PDF.',
      'There is no usable text. AI extraction is required for this PDF.',
    );
  if (verdict.reason === 'QUALITY_LOW')
    return text(
      'Το PDF διαβάστηκε, αλλά η ποιότητα του κειμένου φαίνεται χαμηλή. Προτείνεται εξαγωγή με AI.',
      'The PDF was read, but the text quality appears low. AI extraction is recommended.',
    );
  return text(
    'Το εξαγμένο κείμενο φαίνεται καθαρό. Δεν προτείνεται εξαγωγή με AI.',
    'The extracted text appears clear. AI extraction is not recommended.',
  );
}

function pdfAiTone(
  verdict:
    | {
        decision: 'NOT_NEEDED' | 'RECOMMENDED' | 'REQUIRED';
        reason: 'TEXT_OK' | 'QUALITY_LOW' | 'EMPTY_TEXT';
      }
    | null
    | undefined,
): string {
  if (!verdict) return '';
  if (verdict.decision === 'REQUIRED') return 'text-rose-700 dark:text-rose-300';
  if (verdict.decision === 'RECOMMENDED') return 'text-amber-700 dark:text-amber-300';
  return 'text-emerald-700 dark:text-emerald-300';
}

function pdfAiIcon(
  verdict:
    | {
        decision: 'NOT_NEEDED' | 'RECOMMENDED' | 'REQUIRED';
        reason: 'TEXT_OK' | 'QUALITY_LOW' | 'EMPTY_TEXT';
      }
    | null
    | undefined,
): IconName {
  return verdict?.decision === 'NOT_NEEDED' ? 'check' : 'alert';
}

function relativeTime(value: string | undefined, text: TextFn, locale: 'el' | 'en'): string {
  if (!value) return '—';
  const date = new Date(value);
  const delta = Date.now() - date.getTime();
  if (Number.isNaN(delta)) return '—';
  const minutes = Math.round(delta / 60_000);
  if (minutes < 1) return text('μόλις τώρα', 'just now');
  if (minutes < 60)
    return minutes === 1
      ? text('πριν 1 λεπτό', '1 minute ago')
      : text(`πριν ${minutes} λεπτά`, `${minutes} minutes ago`);
  const hours = Math.round(minutes / 60);
  if (hours < 24)
    return hours === 1
      ? text('πριν 1 ώρα', '1 hour ago')
      : text(`πριν ${hours} ώρες`, `${hours} hours ago`);
  const days = Math.round(hours / 24);
  if (days < 30)
    return days === 1
      ? text('πριν 1 ημέρα', '1 day ago')
      : text(`πριν ${days} ημέρες`, `${days} days ago`);
  return date.toLocaleDateString(locale === 'en' ? 'en-GB' : 'el-GR');
}

function matchesKind(type: string, kind: Kind): boolean {
  if (kind === 'all') return true;
  if (kind === 'pages') return type === 'WP_PAGE' || type === 'WP_POST';
  if (kind === 'pdf') return type === 'PDF' || type === 'WP_PDF';
  if (kind === 'faq') return type === 'FAQ';
  return type === 'TEXT';
}

export function KnowledgePage(props: { canWrite: boolean }) {
  const { text, locale } = useAdminLocale();
  const confirm = useConfirm();
  const sites = useRemote<SiteRow[]>('/v1/sites');
  const [params, setParams] = useSearchParams();
  const requested = params.get('site') ?? '';
  const site = sites.data?.find((row) => row.id === requested) ?? sites.data?.[0];
  const siteId = site?.id ?? '';
  const sources = useRemote<SourceRow[]>(siteId ? `/v1/sites/${siteId}/sources` : null);
  const [kind, setKind] = useState<Kind>('all');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<'all' | Tone | 'review'>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [checked, setChecked] = useState<Set<string>>(() => new Set());
  const [composer, setComposer] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [bulkPending, setBulkPending] = useState(false);

  useEffect(() => {
    if (!site || requested === site.id) return;
    setParams({ site: site.id }, { replace: true });
  }, [requested, setParams, site]);

  useEffect(() => {
    setChecked(new Set());
  }, [siteId, kind, status, query]);

  const rows = sources.data ?? [];
  const readyCount = rows.filter((row) => sourceTone(row) === 'ready').length;
  const reviewCount = rows.length - readyCount;
  const visible = rows.filter((row) => {
    if (!matchesKind(row.type, kind)) return false;
    if (query.trim() && !row.title.toLowerCase().includes(query.trim().toLowerCase())) return false;
    const tone = sourceTone(row);
    if (status === 'review') return tone !== 'ready';
    if (status !== 'all' && tone !== status) return false;
    return true;
  });
  const paging = useClientPaging(visible, {
    resetKey: `${siteId}|${kind}|${status}|${query}`,
  });
  const selected = rows.find((row) => row.id === selectedId) ?? null;
  const pageIds = paging.rows.map((row) => row.id);
  const allVisibleChecked = pageIds.length > 0 && pageIds.every((id) => checked.has(id));
  const someVisibleChecked = pageIds.some((id) => checked.has(id));

  function chooseSite(id: string): void {
    setSelectedId(null);
    setChecked(new Set());
    setNotice(null);
    setParams({ site: id });
  }

  function toggleChecked(id: string): void {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAllVisible(): void {
    setChecked((prev) => {
      if (allVisibleChecked) {
        const next = new Set(prev);
        for (const id of pageIds) next.delete(id);
        return next;
      }
      const next = new Set(prev);
      for (const id of pageIds) next.add(id);
      return next;
    });
  }

  async function bulkAct(action: 'activate' | 'reject'): Promise<void> {
    if (!siteId || checked.size === 0) return;
    const count = checked.size;
    const approved = await confirm({
      title:
        action === 'activate'
          ? text('Ενεργοποίηση πηγών', 'Activate sources')
          : text('Απόρριψη πηγών', 'Reject sources'),
      message:
        action === 'activate'
          ? text(
              `Να ενεργοποιηθούν ${count === 1 ? 'η επιλεγμένη πηγή' : `οι ${count} επιλεγμένες πηγές`};`,
              `Activate ${count === 1 ? 'the selected source' : `the ${count} selected sources`}?`,
            )
          : text(
              `Να απορριφθούν ${count === 1 ? 'η επιλεγμένη πηγή' : `οι ${count} επιλεγμένες πηγές`};\n\nΔεν θα χρησιμοποιούνται πλέον στις απαντήσεις του chatbot.`,
              `Reject ${count === 1 ? 'the selected source' : `the ${count} selected sources`}?\n\nThey will no longer be used in chatbot answers.`,
            ),
      confirmLabel:
        action === 'activate' ? text('Ενεργοποίηση', 'Activate') : text('Απόρριψη', 'Reject'),
      danger: action === 'reject',
    });
    if (!approved) return;
    setBulkPending(true);
    setNotice(null);
    try {
      const sourceIds = [...checked];
      await readJson(`/v1/sites/${siteId}/sources/bulk`, {
        method: 'POST',
        body: JSON.stringify({ action, sourceIds }),
      });
      setChecked(new Set());
      setNotice({
        tone: 'ok',
        text:
          action === 'activate'
            ? sourceIds.length === 1
              ? text('Η επιλεγμένη πηγή ενεργοποιήθηκε.', 'The selected source was activated.')
              : text(
                  `${sourceIds.length} πηγές ενεργοποιήθηκαν.`,
                  `${sourceIds.length} sources were activated.`,
                )
            : sourceIds.length === 1
              ? text('Η επιλεγμένη πηγή απορρίφθηκε.', 'The selected source was rejected.')
              : text(
                  `${sourceIds.length} πηγές απορρίφθηκαν.`,
                  `${sourceIds.length} sources were rejected.`,
                ),
      });
      await sources.mutate();
    } catch (error) {
      setNotice({
        tone: 'error',
        text:
          error instanceof Error
            ? error.message
            : text('Η μαζική ενέργεια απέτυχε', 'The bulk action failed'),
      });
    } finally {
      setBulkPending(false);
    }
  }

  async function requestSync(): Promise<void> {
    if (!siteId) return;
    setSyncing(true);
    setNotice(null);
    try {
      await readJson(`/v1/sites/${siteId}/sync-requests`, { method: 'POST' });
      setNotice({
        tone: 'ok',
        text: text(
          'Το αίτημα καταχωρήθηκε. Το πρόσθετο θα στείλει τις σελίδες που είναι επιλεγμένες στο WordPress.',
          'The request was submitted. The plugin will send the pages selected in WordPress.',
        ),
      });
      await sources.mutate();
    } catch (error) {
      setNotice({
        tone: 'error',
        text:
          error instanceof Error ? error.message : text('Ο συγχρονισμός απέτυχε', 'Sync failed'),
      });
    } finally {
      setSyncing(false);
    }
  }

  return (
    <section className="space-y-6">
      <PageHeader
        description={
          site
            ? text(
                `Πληροφορίες που χρησιμοποιεί το chatbot για το ${site.name}. ${site.canonicalOrigin}`,
                `Information used by the chatbot for ${site.name}. ${site.canonicalOrigin}`,
              )
            : text(
                'Πληροφορίες που χρησιμοποιεί το chatbot, ξεχωριστά για κάθε ιστοσελίδα.',
                'Information used by the chatbot, managed separately for each website.',
              )
        }
        actions={
          <>
            <Select
              aria-label={text('Ιστοσελίδα', 'Website')}
              className="min-w-48"
              value={siteId}
              disabled={!sites.data?.length}
              onChange={chooseSite}
              options={(sites.data ?? []).map((row) => ({
                value: row.id,
                label: row.name,
              }))}
            />
            {props.canWrite && siteId ? (
              <>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={syncing}
                  onClick={() => void requestSync()}
                >
                  <Icon name="refresh" className="size-4" />
                  {syncing ? text('Αίτημα…', 'Requesting…') : text('Συγχρονισμός', 'Sync')}
                </Button>
                <Button type="button" onClick={() => setComposer(true)}>
                  <Icon name="plus" className="size-4" /> {text('Προσθήκη πηγής', 'Add source')}
                </Button>
              </>
            ) : null}
          </>
        }
      />

      {sites.error ? <ErrorNote message={sites.error.message} /> : null}
      {notice ? (
        <p
          className={`text-sm ${notice.tone === 'ok' ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}`}
        >
          {notice.text}
        </p>
      ) : null}
      {composer && siteId ? (
        <AddSourceForm
          siteId={siteId}
          onClose={() => setComposer(false)}
          onCreated={(id) => {
            setComposer(false);
            setSelectedId(id);
            setNotice({
              tone: 'ok',
              text: text(
                'Η πηγή καταχωρήθηκε και μπαίνει σε επεξεργασία.',
                'The source was added and is being processed.',
              ),
            });
            void sources.mutate();
          }}
        />
      ) : null}

      <div className="grid gap-4 sm:grid-cols-3">
        <Stat
          icon="file"
          label={text('Πηγές', 'Sources')}
          value={sources.data ? rows.length : '—'}
        />
        <Stat
          icon="check"
          label={text('Έτοιμες', 'Ready')}
          value={sources.data ? readyCount : '—'}
        />
        <Stat
          icon="alert"
          label={text('Χρειάζονται έλεγχο', 'Need review')}
          value={sources.data ? reviewCount : '—'}
          hint={text(
            'Αποτυχία, επεξεργασία ή πηγή που δεν έχει ενεργοποιηθεί',
            'Failed, processing, or not yet activated',
          )}
        />
      </div>

      {reviewCount > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-950/40 px-4 py-3 text-sm text-amber-950">
          <span className="flex items-start gap-2">
            <Icon name="alert" className="mt-0.5 size-4 shrink-0" />
            <span>
              {reviewCount === 1
                ? text('Μία πηγή χρειάζεται έλεγχο.', 'One source needs review.')
                : text(
                    `${reviewCount} πηγές χρειάζονται έλεγχο.`,
                    `${reviewCount} sources need review.`,
                  )}{' '}
              {text(
                'Δεν χρησιμοποιούνται σε απαντήσεις όσο δεν είναι έτοιμες.',
                'They are not used in answers until they are ready.',
              )}
            </span>
          </span>
          <Button type="button" variant="secondary" onClick={() => setStatus('review')}>
            <Icon name="eye" className="size-4" /> {text('Προβολή', 'View')}
          </Button>
        </div>
      ) : null}

      <div className="space-y-4">
        <div className="overflow-hidden admin-surface">
          <div className="border-b border-slate-100 px-3 py-2.5 dark:border-slate-800">
            <Tabs
              variant="pills"
              items={kindItems(text)}
              value={kind}
              onChange={(id) => setKind(id as Kind)}
            />
          </div>
          <div className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center">
            <TextInput
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={text('Αναζήτηση πηγής…', 'Search sources…')}
              aria-label={text('Αναζήτηση πηγής', 'Search sources')}
              flush
              className="w-full sm:min-w-0 sm:flex-1"
            />
            <Select
              aria-label={text('Κατάσταση', 'Status')}
              className="w-full shrink-0 sm:w-52"
              value={status}
              onChange={(next) => setStatus(next as typeof status)}
              options={[
                { value: 'all', label: text('Όλες οι καταστάσεις', 'All statuses') },
                { value: 'ready', label: text('Έτοιμο', 'Ready') },
                { value: 'processing', label: text('Σε επεξεργασία', 'Processing') },
                { value: 'failed', label: text('Αποτυχία', 'Failed') },
                { value: 'inactive', label: text('Ανενεργό', 'Inactive') },
                { value: 'review', label: text('Χρειάζονται έλεγχο', 'Need review') },
              ]}
            />
          </div>
          {kind === 'pages' ? (
            <p className="border-t border-slate-100 px-3 py-2 text-sm text-slate-500 dark:border-slate-800 dark:text-slate-400">
              {text(
                'Οι σελίδες επιλέγονται στο πρόσθετο WordPress. Ο συγχρονισμός φέρνει όσες είναι επιλεγμένες εκεί.',
                'Pages are selected in the WordPress plugin. Sync imports the pages selected there.',
              )}
            </p>
          ) : null}
        </div>

        {props.canWrite && checked.size > 0 ? (
          <div className="flex flex-wrap items-center gap-3 admin-surface px-4 py-3 text-sm">
            <span className="font-medium text-slate-800 dark:text-slate-200">
              {checked.size === 1
                ? text('1 επιλεγμένη', '1 selected')
                : text(`${checked.size} επιλεγμένες`, `${checked.size} selected`)}
            </span>
            <Button type="button" disabled={bulkPending} onClick={() => void bulkAct('activate')}>
              <Icon name="check" className="size-4" /> {text('Ενεργοποίηση', 'Activate')}
            </Button>
            <Button
              type="button"
              variant="danger"
              disabled={bulkPending}
              onClick={() => void bulkAct('reject')}
            >
              <Icon name="x" className="size-4" /> {text('Απόρριψη', 'Reject')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={bulkPending}
              onClick={() => setChecked(new Set())}
            >
              {text('Καθαρισμός', 'Clear')}
            </Button>
          </div>
        ) : null}

        <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
          <div className="min-w-0 space-y-4">
            <Panel
              loading={!sources.data && !sources.error && Boolean(siteId)}
              error={sources.error?.message ?? null}
              empty={!siteId || visible.length === 0}
              emptyLabel={
                siteId
                  ? text(
                      'Δεν υπάρχουν πηγές σε αυτή την προβολή.',
                      'There are no sources in this view.',
                    )
                  : text('Δεν υπάρχει ιστοσελίδα.', 'There is no website.')
              }
              emptyHint={
                siteId
                  ? text(
                      'Προσθέστε κείμενο, συχνή ερώτηση ή PDF, ή ζητήστε συγχρονισμό από το WordPress.',
                      'Add text, an FAQ, or a PDF, or request a sync from WordPress.',
                    )
                  : text(
                      'Δημιουργήστε ιστοσελίδα από τη σελίδα Ιστοσελίδες.',
                      'Create a website from the Websites page.',
                    )
              }
            >
              <Card flush>
                <PagedTableShell pagination={paging.total > 0 ? paging.pager : null}>
                  <Table
                    head={[
                      props.canWrite ? text('Επιλογή', 'Select') : '·',
                      text('Πηγή', 'Source'),
                      text('Τύπος', 'Type'),
                      text('Προέλευση', 'Origin'),
                      text('Γλώσσα', 'Language'),
                      text('Κατάσταση', 'Status'),
                      text('Ενημέρωση', 'Updated'),
                    ]}
                  >
                    {props.canWrite && paging.rows.length > 0 ? (
                      <tr className="bg-slate-50 dark:bg-slate-950/80">
                        <Td className="w-12">
                          <Checkbox
                            checked={allVisibleChecked}
                            ref={(el) => {
                              if (el) el.indeterminate = someVisibleChecked && !allVisibleChecked;
                            }}
                            onChange={toggleAllVisible}
                            aria-label={text('Επιλογή όλων στη σελίδα', 'Select all on this page')}
                          />
                        </Td>
                        <Td muted className="text-xs">
                          {allVisibleChecked
                            ? text('Αποεπιλογή σελίδας', 'Deselect page')
                            : text('Επιλογή όλων στη σελίδα', 'Select all on page')}
                        </Td>
                        <Td>{null}</Td>
                        <Td>{null}</Td>
                        <Td>{null}</Td>
                        <Td>{null}</Td>
                        <Td>{null}</Td>
                      </tr>
                    ) : null}
                    {paging.rows.map((source) => {
                      const tone = sourceTone(source);
                      const isChecked = checked.has(source.id);
                      return (
                        <tr
                          key={source.id}
                          onClick={() => setSelectedId(source.id)}
                          className={cx(
                            'cursor-pointer',
                            selectedId === source.id
                              ? 'bg-blue-50 dark:bg-blue-950/40'
                              : 'hover:bg-slate-50 dark:hover:bg-slate-800/70',
                            isChecked && selectedId !== source.id
                              ? 'bg-slate-50 dark:bg-slate-950'
                              : null,
                          )}
                        >
                          <Td className="w-12">
                            {props.canWrite ? (
                              <Checkbox
                                checked={isChecked}
                                onClick={(event) => event.stopPropagation()}
                                onChange={() => toggleChecked(source.id)}
                                aria-label={text(
                                  `Επιλογή ${source.title}`,
                                  `Select ${source.title}`,
                                )}
                              />
                            ) : null}
                          </Td>
                          <Td>
                            <span className="flex items-center gap-3">
                              <span
                                className={`grid size-9 shrink-0 place-items-center rounded-full ${toneClass(tone)}`}
                              >
                                <Icon name={typeIcon(source.type)} className="size-4" />
                              </span>
                              <button
                                type="button"
                                className="text-left font-medium text-slate-900 dark:text-slate-100"
                                onClick={() => setSelectedId(source.id)}
                              >
                                {source.title}
                              </button>
                            </span>
                          </Td>
                          <Td muted>{typeLabel(source.type, text)}</Td>
                          <Td muted>{originLabel(source.origin, text)}</Td>
                          <Td muted>{source.locale ? localeLabel(source.locale, text) : '—'}</Td>
                          <Td>
                            <Badge value={TONE_BADGE[tone]} label={toneLabel(tone, text)} />
                          </Td>
                          <Td muted>{relativeTime(source.updatedAt, text, locale)}</Td>
                        </tr>
                      );
                    })}
                  </Table>
                </PagedTableShell>
              </Card>
            </Panel>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {text(
                'Προσθέστε πηγές στα ελληνικά, αγγλικά ή γαλλικά. Το chatbot απαντά από το ενεργό κείμενο της ιστοσελίδας.',
                'Add sources in Greek, English, or French. The chatbot answers using the website’s active content.',
              )}
            </p>
          </div>

          <SourcePreviewCard
            siteId={siteId}
            source={selected}
            canWrite={props.canWrite}
            onChanged={() => {
              void sources.mutate();
            }}
            onDeleted={() => {
              setSelectedId(null);
              void sources.mutate();
            }}
          />
        </div>
      </div>
    </section>
  );
}

function SourcePreviewCard(props: {
  siteId: string;
  source: SourceRow | null;
  canWrite: boolean;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const { text, locale } = useAdminLocale();
  const remote = useRemote<SourcePreview>(
    props.siteId && props.source ? `/v1/sites/${props.siteId}/sources/${props.source.id}` : null,
  );
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionsOpen, setActionsOpen] = useState(false);
  const actionsRef = useRef<HTMLDivElement>(null);
  const confirm = useConfirm();
  const tone = props.source ? sourceTone(props.source) : null;
  const link = props.source?.canonicalUrl;
  const safeLink = link && /^https?:\/\//.test(link) ? link : null;
  const pdfAi = remote.data?.pdfAiRecommendation;
  const pdfAiText = pdfAiMessage(pdfAi, text);

  useEffect(() => {
    setActionsOpen(false);
  }, [props.source?.id]);

  useEffect(() => {
    if (!actionsOpen) return;
    function closeActions(event: MouseEvent): void {
      if (!actionsRef.current?.contains(event.target as Node)) setActionsOpen(false);
    }
    function closeOnEscape(event: KeyboardEvent): void {
      if (event.key === 'Escape') setActionsOpen(false);
    }
    document.addEventListener('mousedown', closeActions);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeActions);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [actionsOpen]);

  async function act(action: 'activate' | 'reject'): Promise<void> {
    if (!props.source) return;
    const approved = await confirm({
      title:
        action === 'activate'
          ? text('Ενεργοποίηση πηγής', 'Activate source')
          : text('Απόρριψη πηγής', 'Reject source'),
      message:
        action === 'activate'
          ? text(
              `Να ενεργοποιηθεί η πηγή «${props.source.title}» και να χρησιμοποιείται στις απαντήσεις του chatbot;`,
              `Activate the source “${props.source.title}” and use it in chatbot answers?`,
            )
          : text(
              `Να απορριφθεί η πηγή «${props.source.title}»;\n\nΔεν θα χρησιμοποιείται πλέον στις απαντήσεις του chatbot.`,
              `Reject the source “${props.source.title}”?\n\nIt will no longer be used in chatbot answers.`,
            ),
      confirmLabel:
        action === 'activate' ? text('Ενεργοποίηση', 'Activate') : text('Απόρριψη', 'Reject'),
      danger: action === 'reject',
    });
    if (!approved) return;
    setPending(true);
    setError(null);
    try {
      await readJson(`/v1/sites/${props.siteId}/sources/${props.source.id}/${action}`, {
        method: 'POST',
      });
      props.onChanged();
      await remote.mutate();
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : text('Η ενέργεια απέτυχε', 'The action failed'),
      );
    } finally {
      setPending(false);
    }
  }

  async function runAiExtract(): Promise<void> {
    if (!props.source) return;
    const confirmed = await confirm({
      title: text('Εξαγωγή κειμένου με AI', 'Extract text with AI'),
      message: text(
        `Να εξαχθεί ξανά το κείμενο της πηγής «${props.source.title}» με AI όραση;\n\nΗ ενέργεια χρησιμοποιεί τον πάροχο AI της πλατφόρμας και χρεώνεται στο budget. Το τοπικό OCR παραμένει το αυτόματο fallback.`,
        `Extract the text from “${props.source.title}” again using AI vision?\n\nThis action uses the platform AI provider and is charged to the budget. Local OCR remains the automatic fallback.`,
      ),
      confirmLabel: text('Εξαγωγή με AI', 'Extract with AI'),
    });
    if (!confirmed) return;
    setPending(true);
    setError(null);
    try {
      await readJson(`/v1/sites/${props.siteId}/sources/${props.source.id}/ai-extract`, {
        method: 'POST',
      });
      props.onChanged();
      await remote.mutate();
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : text('Η εξαγωγή με AI απέτυχε', 'AI extraction failed'),
      );
    } finally {
      setPending(false);
    }
  }

  async function openPdf(): Promise<void> {
    if (!props.source) return;
    setPending(true);
    setError(null);
    try {
      await openAuthenticatedPdf(`/v1/sites/${props.siteId}/sources/${props.source.id}/file`);
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : text('Το PDF δεν μπόρεσε να ανοίξει.', 'The PDF could not be opened.'),
      );
    } finally {
      setPending(false);
    }
  }

  async function deleteSource(): Promise<void> {
    if (!props.source) return;
    const approved = await confirm({
      title: text('Διαγραφή πηγής', 'Delete source'),
      message: text(
        `Να διαγραφεί οριστικά η πηγή «${props.source.title}»;\n\nΘα αφαιρεθούν το κείμενο, τα chunks και το αποθηκευμένο αρχείο όπου υπάρχει.`,
        `Permanently delete the source “${props.source.title}”?\n\nIts text, chunks, and stored file, where present, will be removed.`,
      ),
      confirmLabel: text('Διαγραφή', 'Delete'),
      danger: true,
    });
    if (!approved) return;
    setPending(true);
    setError(null);
    try {
      await readJson(`/v1/sites/${props.siteId}/sources/${props.source.id}`, {
        method: 'DELETE',
      });
      props.onDeleted();
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : text('Η διαγραφή απέτυχε', 'Deletion failed'),
      );
      setPending(false);
    }
  }

  return (
    <Card
      title={text('Προεπισκόπηση πηγής', 'Source preview')}
      description={text(
        'Το κείμενο που εξήχθη και μπορεί να χρησιμοποιηθεί σε απαντήσεις.',
        'The extracted text that can be used in answers.',
      )}
      className="xl:sticky xl:top-24"
    >
      {!props.source ? (
        <p className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500">
          {text('Επιλέξτε μια πηγή από τη λίστα.', 'Select a source from the list.')}
        </p>
      ) : (
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <span
              className={`grid size-9 shrink-0 place-items-center rounded-full ${tone ? toneClass(tone) : ''}`}
            >
              <Icon name={typeIcon(props.source.type)} className="size-4" />
            </span>
            <div className="min-w-0">
              <p className="font-medium text-slate-900 dark:text-slate-100">{props.source.title}</p>
              {safeLink ? (
                <a
                  href={safeLink}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-0.5 block truncate text-sm text-blue-600 hover:text-blue-800 dark:hover:text-blue-200"
                >
                  {safeLink}
                </a>
              ) : null}
            </div>
            {props.canWrite &&
            (props.source.status !== 'DISABLED' ||
              (remote.data?.canDelete ?? props.source.canDelete)) ? (
              <div ref={actionsRef} className="relative ml-auto shrink-0">
                <button
                  type="button"
                  aria-label={text('Περισσότερες ενέργειες', 'More actions')}
                  aria-haspopup="menu"
                  aria-expanded={actionsOpen}
                  disabled={pending}
                  onClick={() => setActionsOpen((open) => !open)}
                  className="grid size-9 place-items-center rounded-lg border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--admin-accent)]/40 disabled:opacity-45 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
                >
                  <Icon name="more" className="size-4" />
                </button>
                {actionsOpen ? (
                  <div
                    role="menu"
                    className="absolute top-11 right-0 z-20 w-40 overflow-hidden rounded-lg border border-slate-200 bg-white p-1 shadow-lg dark:border-slate-700 dark:bg-slate-900"
                  >
                    {props.source.status !== 'DISABLED' ? (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setActionsOpen(false);
                          void act('reject');
                        }}
                        className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-rose-700 hover:bg-rose-50 dark:text-rose-300 dark:hover:bg-rose-950/40"
                      >
                        <Icon name="x" className="size-4" /> {text('Απόρριψη', 'Reject')}
                      </button>
                    ) : null}
                    {(remote.data?.canDelete ?? props.source.canDelete) ? (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setActionsOpen(false);
                          void deleteSource();
                        }}
                        className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-rose-700 hover:bg-rose-50 dark:text-rose-300 dark:hover:bg-rose-950/40"
                      >
                        <Icon name="trash" className="size-4" /> {text('Διαγραφή', 'Delete')}
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="space-y-2 border-b border-slate-200 pb-4 dark:border-slate-700">
            <div className="grid grid-cols-2 gap-2">
              {props.source.type === 'PDF' || props.source.type === 'WP_PDF' ? (
                <Button
                  type="button"
                  disabled={pending}
                  className="px-2.5"
                  onClick={() => void openPdf()}
                >
                  <Icon name="file" className="size-4" /> {text('Άνοιγμα PDF', 'Open PDF')}
                </Button>
              ) : null}
              {props.canWrite && tone !== 'ready' ? (
                <Button
                  type="button"
                  disabled={pending}
                  className="px-2.5"
                  onClick={() => void act('activate')}
                >
                  <Icon name="check" className="size-4" /> {text('Ενεργοποίηση', 'Activate')}
                </Button>
              ) : null}
              {props.canWrite &&
              (remote.data?.canAiExtract ||
                props.source.type === 'PDF' ||
                props.source.type === 'WP_PDF') ? (
                <Button
                  type="button"
                  variant="secondary"
                  disabled={pending}
                  className="px-2.5"
                  onClick={() => void runAiExtract()}
                >
                  <Icon name="sparkles" className="size-4" /> {text('Εξαγωγή AI', 'AI extraction')}
                </Button>
              ) : null}
            </div>
            <Link
              to={`/sites/${props.siteId}/sources/${props.source.id}`}
              className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 dark:hover:text-blue-200"
            >
              {text('Πλήρης προβολή', 'Full view')} <Icon name="arrow" className="size-4" />
            </Link>
          </div>
          {error ? <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p> : null}
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500">
                {text('Τύπος', 'Type')}
              </dt>
              <dd className="mt-0.5 font-medium text-slate-900 dark:text-slate-100">
                {typeLabel(props.source.type, text)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500">
                {text('Γλώσσα', 'Language')}
              </dt>
              <dd className="mt-0.5 font-medium text-slate-900 dark:text-slate-100">
                {props.source.locale ? localeLabel(props.source.locale, text) : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500">
                {text('Προέλευση', 'Origin')}
              </dt>
              <dd className="mt-0.5 font-medium text-slate-900 dark:text-slate-100">
                {originLabel(props.source.origin, text)}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500">
                {text('Κατάσταση', 'Status')}
              </dt>
              <dd className="mt-1">
                {tone ? <Badge value={TONE_BADGE[tone]} label={toneLabel(tone, text)} /> : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500">
                {text('Ενημέρωση', 'Updated')}
              </dt>
              <dd className="mt-0.5 font-medium text-slate-900 dark:text-slate-100">
                {relativeTime(props.source.updatedAt, text, locale)}
              </dd>
            </div>
          </dl>
          {tone === 'ready' ? (
            <p className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-300">
              <Icon name="check" className="size-4" />{' '}
              {text(
                'Το περιεχόμενο είναι διαθέσιμο στο chatbot.',
                'The content is available to the chatbot.',
              )}
            </p>
          ) : (
            <p className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500">
              {text(
                'Δεν χρησιμοποιείται σε απαντήσεις μέχρι η έκδοση να είναι έτοιμη και ενεργή.',
                'It is not used in answers until the version is ready and active.',
              )}
            </p>
          )}
          {props.source.origin === 'WORDPRESS' ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {text(
                'Η πηγή διαχειρίζεται από το WordPress. Για αφαίρεση, αποεπιλέξτε την από το πρόσθετο και εκτελέστε συγχρονισμό.',
                'This source is managed in WordPress. To remove it, deselect it in the plugin and run a sync.',
              )}
            </p>
          ) : null}
          {pdfAiText ? (
            <p className={`flex items-start gap-2 text-sm ${pdfAiTone(pdfAi)}`}>
              <Icon name={pdfAiIcon(pdfAi)} className="mt-0.5 size-4 shrink-0" />
              <span>{pdfAiText}</span>
            </p>
          ) : null}
          {remote.error ? <ErrorNote message={remote.error.message} /> : null}
          {remote.data?.preview?.text ? (
            <pre className="max-h-72 overflow-auto rounded-lg bg-slate-50 dark:bg-slate-950 p-3 text-sm leading-6 whitespace-pre-wrap text-slate-800 dark:text-slate-200">
              {remote.data.preview.text}
            </pre>
          ) : remote.data ? (
            <p className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500">
              {text('Δεν υπάρχει ακόμη εξαγμένο κείμενο.', 'No text has been extracted yet.')}
            </p>
          ) : (
            <p className="text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500">
              {text('Φόρτωση κειμένου…', 'Loading text…')}
            </p>
          )}
        </div>
      )}
    </Card>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function AddSourceForm(props: {
  siteId: string;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const { text } = useAdminLocale();
  const [type, setType] = useState<'FAQ' | 'TEXT' | 'PDF'>('FAQ');
  const [title, setTitle] = useState('');
  const [locale, setLocale] = useState<'el' | 'en' | 'fr'>('el');
  const [bodyText, setBodyText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [uploadStage, setUploadStage] = useState<
    'reading' | 'uploading' | 'processing' | 'saving' | null
  >(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const uploadAbort = useRef<AbortController | null>(null);
  const { pluginMenuName } = useUiPrefs();

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      if (type === 'PDF') {
        if (!file) throw new Error(text('Επιλέξτε αρχείο PDF.', 'Select a PDF file.'));
        if (file.size > 10 * 1024 * 1024)
          throw new Error(text('Το PDF ξεπερνά τα 10 MB.', 'The PDF exceeds 10 MB.'));
        const controller = new AbortController();
        uploadAbort.current = controller;
        setUploadStage('reading');
        const bytes = await file.arrayBuffer();
        const created = await uploadRawJson<{ id: string }>(
          `/v1/sites/${props.siteId}/uploads`,
          bytes,
          {
            contentType: 'application/pdf',
            timeoutMs: 5 * 60_000,
            signal: controller.signal,
            onStage: setUploadStage,
            onProgress: (loaded, total) => {
              setUploadProgress(total ? Math.min(100, Math.round((loaded / total) * 100)) : null);
            },
          },
        );
        uploadAbort.current = null;
        setUploadStage('saving');
        if (title.trim() || locale) {
          await readJson(`/v1/sites/${props.siteId}/sources/${created.id}`, {
            method: 'PATCH',
            body: JSON.stringify({
              ...(title.trim() ? { title: title.trim() } : {}),
              locale,
            }),
          });
        }
        props.onCreated(created.id);
        return;
      }
      const created = await readJson<{ id: string }>(`/v1/sites/${props.siteId}/sources`, {
        method: 'POST',
        body: JSON.stringify({ type, title: title.trim(), text: bodyText.trim(), locale }),
      });
      props.onCreated(created.id);
    } catch (caught) {
      setError(
        caught instanceof DOMException && caught.name === 'AbortError'
          ? text('Η μεταφόρτωση διακόπηκε.', 'The upload was cancelled.')
          : caught instanceof Error
            ? caught.message
            : text('Η καταχώριση απέτυχε', 'Creation failed'),
      );
    } finally {
      uploadAbort.current = null;
      setUploadStage(null);
      setUploadProgress(null);
      setPending(false);
    }
  }

  function cancel(): void {
    if (pending) {
      uploadAbort.current?.abort();
      return;
    }
    props.onClose();
  }

  function uploadStageText(): string | null {
    if (uploadStage === 'reading') return text('Προετοιμασία αρχείου…', 'Preparing file…');
    if (uploadStage === 'uploading')
      return uploadProgress === null
        ? text('Μεταφόρτωση αρχείου…', 'Uploading file…')
        : text(`Μεταφόρτωση αρχείου… ${uploadProgress}%`, `Uploading file… ${uploadProgress}%`);
    if (uploadStage === 'processing')
      return text(
        'Το αρχείο μεταφορτώθηκε. Γίνεται εξαγωγή κειμένου και, αν χρειάζεται, OCR…',
        'The file was uploaded. Text extraction and, if needed, OCR are in progress…',
      );
    if (uploadStage === 'saving') return text('Αποθήκευση πηγής…', 'Saving source…');
    return null;
  }

  const kinds = [
    {
      id: 'TEXT' as const,
      title: text('Σελίδα website', 'Website page'),
      text: text(
        'Οι σελίδες επιλέγονται στο WordPress και έρχονται με συγχρονισμό.',
        'Pages are selected in WordPress and imported by sync.',
      ),
      icon: 'globe' as const,
    },
    {
      id: 'PDF' as const,
      title: text('Αρχείο PDF', 'PDF file'),
      text: text(
        'Μεταφόρτωση εγγράφου με αυτόματη εξαγωγή κειμένου και OCR.',
        'Upload a document with automatic text extraction and OCR.',
      ),
      icon: 'file' as const,
    },
    {
      id: 'FAQ' as const,
      title: 'FAQ',
      text: text('Καταχώριση ερώτησης και απάντησης.', 'Add a question and answer.'),
      icon: 'help' as const,
    },
  ];
  return (
    <Modal
      wide
      title={text('Προσθήκη πηγής γνώσης', 'Add knowledge source')}
      description={text(
        'Μετά την καταχώριση η πηγή περνά από έλεγχο πριν χρησιμοποιηθεί στις απαντήσεις.',
        'After it is added, the source is reviewed before being used in answers.',
      )}
      onClose={cancel}
    >
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        {kinds.map((kind) => (
          <button
            key={kind.id}
            type="button"
            onClick={() => setType(kind.id)}
            className={
              type === kind.id
                ? 'rounded-xl border-2 border-blue-600 bg-blue-50 dark:bg-blue-950/40 px-4 py-3 text-left'
                : 'rounded-[var(--admin-radius)] border border-[color:var(--admin-border)] px-4 py-3 text-left hover:border-slate-300 dark:border-slate-700'
            }
          >
            <span
              className={cx(
                'mb-2 grid size-8 place-items-center rounded-lg',
                type === kind.id
                  ? 'bg-white dark:bg-slate-900 text-blue-700 dark:text-blue-300'
                  : 'bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-400 dark:text-slate-500',
              )}
            >
              <Icon name={kind.icon} className="size-4" />
            </span>
            <span className="block text-sm font-semibold text-slate-900 dark:text-slate-100">
              {kind.title}
            </span>
            <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500">
              {kind.text}
            </span>
          </button>
        ))}
      </div>
      {type === 'TEXT' ? (
        <div className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-400 dark:text-slate-500">
            {text(
              `Οι σελίδες του website δεν ανεβαίνουν από εδώ. Επιλέγονται στο plugin ${pluginMenuName} και συγχρονίζονται.`,
              `Website pages are not uploaded here. Select them in the ${pluginMenuName} plugin and sync them.`,
            )}
          </p>
          <div className="flex justify-end">
            <Button type="button" variant="secondary" onClick={props.onClose}>
              <Icon name="x" className="size-4" /> {text('Κλείσιμο', 'Close')}
            </Button>
          </div>
        </div>
      ) : (
        <form className="grid gap-4" onSubmit={(event) => void submit(event)}>
          {type === 'PDF' ? (
            <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-600 px-4 py-6 text-sm text-slate-600 dark:text-slate-400 dark:text-slate-500">
              <p className="font-medium text-slate-800 dark:text-slate-200">
                {file ? file.name : text('Επιλέξτε αρχείο PDF', 'Select a PDF file')}
              </p>
              {file ? (
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500">
                  {formatBytes(file.size)}
                </p>
              ) : (
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 dark:text-slate-500">
                  {text(
                    'Έως 10 MB και 200 σελίδες. Τα σαρωμένα PDF περνούν αυτόματα από OCR και, όταν χρειάζεται, προτείνεται εξαγωγή με AI.',
                    'Up to 10 MB and 200 pages. Scanned PDFs automatically go through OCR, and AI extraction is recommended when needed.',
                  )}
                </p>
              )}
              <FileField
                className="mt-4"
                accept="application/pdf,.pdf"
                label={text('Επιλογή PDF', 'Choose PDF')}
                emptyLabel={file ? file.name : text('Δεν έχει επιλεγεί αρχείο', 'No file selected')}
                onChange={(next) => setFile(next)}
              />
            </div>
          ) : null}
          <Field label={type === 'PDF' ? text('Τίτλος', 'Title') : text('Ερώτηση', 'Question')}>
            <TextInput
              required={type !== 'PDF'}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </Field>
          <Field label={text('Γλώσσα περιεχομένου', 'Content language')}>
            <Select
              className="mt-1.5"
              value={locale}
              onChange={(next) => setLocale(next as typeof locale)}
              options={[
                { value: 'el', label: text('Ελληνικά', 'Greek') },
                { value: 'en', label: text('Αγγλικά', 'English') },
                { value: 'fr', label: text('Γαλλικά', 'French') },
              ]}
            />
          </Field>
          {type === 'FAQ' ? (
            <Field label={text('Απάντηση', 'Answer')}>
              <Textarea
                required
                rows={5}
                value={bodyText}
                onChange={(event) => setBodyText(event.target.value)}
              />
            </Field>
          ) : null}
          {uploadStageText() ? (
            <div
              className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-200"
              role="status"
              aria-live="polite"
            >
              <p>{uploadStageText()}</p>
              {uploadStage === 'uploading' && uploadProgress !== null ? (
                <div
                  className="mt-2 h-1.5 overflow-hidden rounded-full bg-blue-100 dark:bg-blue-900"
                  aria-hidden="true"
                >
                  <div
                    className="h-full bg-blue-600 transition-[width]"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              ) : null}
            </div>
          ) : null}
          {error ? <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p> : null}
          <div className="flex justify-end gap-2 border-t border-slate-100 dark:border-slate-800 pt-4">
            <Button
              type="button"
              variant="secondary"
              disabled={pending && uploadAbort.current === null}
              onClick={cancel}
            >
              <Icon name="x" className="size-4" />{' '}
              {pending && uploadAbort.current ? text('Διακοπή', 'Stop') : text('Άκυρο', 'Cancel')}
            </Button>
            <Button type="submit" disabled={pending}>
              <Icon name="plus" className="size-4" />
              {pending ? text('Αποθήκευση…', 'Saving…') : text('Προσθήκη πηγής', 'Add source')}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
